import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button } from '../atoms';
import { CreateEntityModal, SchemaField } from '../organisms';
import { EntityHeader } from '../shell';

/**
 * GenericDetail — schema-driven detail page for any entity table.
 *
 * Auto-renders all visible non-hidden fields as InfoCard rows grouped
 * by `field.section`. Edit button opens CreateEntityModal in edit mode.
 * Matches the v1 SPA openDetail() generic pattern.
 *
 * Phase 11 Block 9 PR 2 (2026-05-12).
 */

export interface GenericDetailConfig {
  table: string;
  routePath?: string;
  apiPath?: string;
  title: string;                                      // base title — actual title pulls from row.name / row.account_number / row.id
  listPath?: string;                                  // default: /<routePath>
  disableEdit?: boolean;                              // hide Edit button (audit_logs append-only; users via invitation flow)
}

export function GenericDetailByConfig(config: GenericDetailConfig) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const apiPath = (config.apiPath ?? config.table).replace(/_/g, '-');
  const routePath = (config.routePath ?? config.table).replace(/_/g, '-');
  const listPath = config.listPath ?? `/${routePath}`;

  const schemaQuery = useQuery<{ fields: SchemaField[] }>({
    queryKey: ['schema', config.table],
    queryFn: ({ signal }) => api.get(`/api/schema/${config.table}`, signal),
    staleTime: 5 * 60_000,
  });

  const recordQuery = useQuery<Record<string, unknown>>({
    queryKey: [config.table, id],
    queryFn: ({ signal }) => api.get(`/api/${apiPath}/${id}`, signal),
    enabled: !!id,
  });

  if (schemaQuery.isLoading || recordQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading {config.title.toLowerCase()}…</p>;
  }

  const error = schemaQuery.error || recordQuery.error;
  if (error) {
    const notFound = error instanceof ApiClientError && error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>
          {notFound ? `${config.title} not found` : `Failed to load ${config.title.toLowerCase()}`}
        </h2>
        <p style={{ margin: 0 }}>{(error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate(listPath)}>
            ← Back to {config.title}
          </Button>
        </p>
      </div>
    );
  }

  if (!schemaQuery.data || !recordQuery.data) return null;

  const row = recordQuery.data;
  const fields = schemaQuery.data.fields
    .filter(f => !f.hidden && !['organization_id', 'created_by_user_id'].includes(f.name))
    .sort((a, b) => a.displayOrder - b.displayOrder);

  // Derive a display title: prefer `name`, then `account_number`, then a sliced UUID
  const title =
    (typeof row.name === 'string' && row.name)
    || (typeof row.account_number === 'string' && row.account_number)
    || (typeof row.id === 'string' && row.id.slice(0, 8))
    || config.title;

  // Group fields by section
  const sections = new Map<string, SchemaField[]>();
  for (const f of fields) {
    const key = f.section ?? 'Details';
    if (!sections.has(key)) sections.set(key, []);
    sections.get(key)!.push(f);
  }

  return (
    <>
      <EntityHeader
        prefix={singular(config.title)}
        title={String(title)}
        statusPills={
          typeof row.status === 'string' ? (
            <Badge variant="neutral">{row.status.replace(/_/g, ' ')}</Badge>
          ) : undefined
        }
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate(listPath)}>
              ← Back
            </Button>
            {!config.disableEdit && (
              <Button variant="primary" size="sm" onClick={() => setEditOpen(true)}>
                Edit
              </Button>
            )}
          </>
        }
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 'var(--sp-4)',
      }}>
        {Array.from(sections.entries()).map(([sectionLabel, sectionFields]) => (
          <InfoCard key={sectionLabel} label={sectionLabel}>
            {sectionFields.map(field => (
              <Row key={field.name} label={field.label} value={formatValue(field, row[field.name])} />
            ))}
          </InfoCard>
        ))}
      </div>

      {id && !config.disableEdit && (
        <CreateEntityModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          table={config.table}
          routePath={apiPath}
          recordId={id}
          invalidateKeys={[[`${apiPath}-list`], [config.table, id]]}
        />
      )}
    </>
  );
}

function singular(title: string): string {
  return title.endsWith('s') ? title.slice(0, -1) : title;
}

function formatValue(field: SchemaField, value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') return '—';
  if (field.dataType === 'boolean') {
    return value ? <Badge variant="success" dot>Yes</Badge> : <span style={{ color: 'var(--color-ink-3)' }}>No</span>;
  }
  if (field.enumOptions && Array.isArray(field.enumOptions)) {
    const opt = field.enumOptions.find(o => o.value === value);
    return opt?.label ?? String(value);
  }
  if (field.dataType === 'number' || field.dataType === 'integer') {
    return typeof value === 'number' ? value.toLocaleString() : String(value);
  }
  if (field.dataType === 'timestamp' && typeof value === 'string') {
    return new Date(value).toLocaleString();
  }
  return String(value);
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <h3 style={{
        margin: '0 0 var(--sp-3)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>{label}</h3>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 'var(--sp-3)',
      padding: 'var(--sp-1) 0',
      fontSize: 'var(--fs-sm)',
      borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{
        color: 'var(--color-ink)',
        fontWeight: 'var(--fw-medium)',
        textAlign: 'right',
        wordBreak: 'break-word',
        maxWidth: '60%',
      }}>{value}</span>
    </div>
  );
}
